import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';

import { buildCssVar } from 'utils/style-override';
import { useHeadingFontOverrides, useTextFontOverrides } from 'utils/font-override';
import { buildImgProps } from 'utils/media';

import type { ComponentsFinalCta } from 'queries/graphql';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

import styles from './styles.module.scss';

export default function FinalCta({
  sectionId,
  title,
  subtitle,
  image,
  text,
  url,
  guaranteeText,
  buttonText,
  style,
}: ComponentsFinalCta) {
  const {
    backgroundColor,
    textColor,
    titleColor,
    subtitleColor,
    buttonBackgroundColor,
    buttonTextColor,
    buttonHoverBackgroundColor,
    buttonBorderColor,
    buttonHoverTextColor,
    buttonHoverBorderColor,
    titleFont,
    textFont,
  } = style || {};

  const headingFontOverrides = useHeadingFontOverrides(titleFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  return (
    <div id={sectionId} className={`root ${styles.container} finalCta`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--title-color', titleColor, 'var(--text-color-dark)')}
          ${buildCssVar('--subtitle-color', subtitleColor, 'var(--text-color-dark)')}
          ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
          ${buildCssVar('--button-color', buttonBackgroundColor, 'var(--primary-color)')}
          ${buildCssVar(
            '--button-on-hover-color',
            buttonHoverBackgroundColor,
            'var(--primary-color)'
          )}
          ${buildCssVar(
            '--button-on-hover-text-color',
            buttonHoverTextColor,
            'var(--text-color-light)'
          )}
          ${buildCssVar(
            '--button-border-color',
            buttonBorderColor,
            buttonBackgroundColor,
            'var(--primary-color)'
          )}
          ${buildCssVar(
            '--button-on-hover-border-color',
            buttonHoverBorderColor,
            buttonHoverBackgroundColor,
            'var(--primary-color)'
          )}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1>{title}</h1>
          <h3>{subtitle}</h3>
        </div>
        <div className={styles.two_column}>
          {image && (
            <div className={styles.img_wrapper}>
              <Image {...buildImgProps(image)} alt={image.alt} />
            </div>
          )}
          <div className={styles.text_wrapper}>
            {text && <MarkdownText text={text} />}
            {/* For now, we're dropping support for direct HTML */}
            {/* {formattedText && <div dangerouslySetInnerHTML={{ __html: formattedText }} />} */}
            {url && (
              <div className={styles.btn}>
                <Link href={url}>{buttonText}</Link>
              </div>
            )}
            {guaranteeText && <p className={styles.guarantee_text}>{guaranteeText}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
